/**
 * Poseidon dashboard — Trades and analytics (§3.2 B).
 *
 * Filters (date / session / strategy / symbol / outcome / exit reason), CSV
 * export, and every chart the spec lists. Charts are fetched one per panel from
 * `/analytics/charts/{chart_id}` so the daemon owns the aggregation — the browser
 * never recomputes a metric that the ledger already knows.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AnalyticsChartId, ClosedTradeRow, TradeFilters } from '../api/types';
import { CHART_KINDS, ChartPanel } from '../components/charts';
import { CsvExportButton, DataTable } from '../components/DataTable';
import type { TableColumn } from '../components/DataTable';
import { Badge, ErrorState, LoadingState, Panel, Unavailable } from '../components/primitives';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';
import { EXIT_REASON_LABELS, OUTCOME_LABELS, toneForOutcome } from '../lib/labels';
import { formatDateTimeWithZone, formatDuration, formatNumber, formatSigned } from '../lib/format';

const CHART_IDS: readonly AnalyticsChartId[] = [
  'cumulative_realized_pnl',
  'equity_curve',
  'win_loss_distribution',
  'pnl_by_hour',
  'pnl_by_weekday',
  'pnl_by_instrument',
  'pnl_by_strategy',
  'pnl_by_regime',
  'slippage_distribution',
  'holding_time_distribution',
  'consecutive_streaks',
  'session_trade_count_winrate',
];

const ALL_SYMBOLS = ['EURUSD', 'GBPUSD', 'USDJPY', 'XAUUSD'];
const ALL_STRATEGIES = ['orb-v1.0.0', 'vwap-reversion-v1.0.0', 'momentum-continuation-v1.0.0'];
const ALL_SESSIONS = ['opening', 'mid_session', 'pre_close'] as const;
const ALL_OUTCOMES = ['win', 'loss', 'breakeven'] as const;
const ALL_EXIT_REASONS = ['stop', 'target', 'time', 'signal', 'risk', 'manual', 'session_flatten'] as const;

interface FilterDraft {
  from: string;
  to: string;
  sessions: string[];
  strategies: string[];
  symbols: string[];
  outcomes: string[];
  exit_reasons: string[];
}

const EMPTY_DRAFT: FilterDraft = { from: '', to: '', sessions: [], strategies: [], symbols: [], outcomes: [], exit_reasons: [] };

function toFilters(draft: FilterDraft): TradeFilters {
  return {
    from: draft.from === '' ? null : new Date(`${draft.from}T00:00:00Z`).toISOString(),
    to: draft.to === '' ? null : new Date(`${draft.to}T23:59:59Z`).toISOString(),
    sessions: draft.sessions as TradeFilters['sessions'],
    strategies: draft.strategies,
    symbols: draft.symbols,
    outcomes: draft.outcomes as TradeFilters['outcomes'],
    exit_reasons: draft.exit_reasons as TradeFilters['exit_reasons'],
  };
}

function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  return (
    <label>
      {label}
      <select
        multiple
        size={Math.min(5, Math.max(3, options.length))}
        value={[...selected]}
        onChange={(event) => onChange([...event.target.selectedOptions].map((option) => option.value))}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

/** One chart panel: owns its own fetch so the grid stays declarative. */
function ChartSlot({ chartId, filters, mock }: { chartId: AnalyticsChartId; filters: TradeFilters; mock: boolean }): JSX.Element {
  const { api } = useServices();
  const key = `chart-${chartId}-${JSON.stringify(filters)}`;
  const chart = useApiData(key, () => api.getChart(chartId, filters), { mode: api.mode });

  if (chart.error !== null && chart.data === null) {
    return (
      <Panel title={chartId.replace(/_/g, ' ')} subtitle="chart unavailable">
        <ErrorState error={chart.error} context="This chart could not be loaded." onRetry={chart.refresh} />
      </Panel>
    );
  }
  if (chart.data === null) {
    return (
      <Panel title={chartId.replace(/_/g, ' ')}>
        <LoadingState label="Loading chart…" />
      </Panel>
    );
  }
  return <ChartPanel series={chart.data} kind={CHART_KINDS[chartId]} mock={mock} />;
}

export function TradesAnalytics(): JSX.Element {
  const { api, isMock } = useServices();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<FilterDraft>(EMPTY_DRAFT);
  const [extraRows, setExtraRows] = useState<ClosedTradeRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const filters = useMemo(() => toFilters(applied), [applied]);
  const filterKey = JSON.stringify(applied);

  const trades = useApiData(`trades-${filterKey}`, () => api.getClosedTrades(filters), { mode: api.mode });

  const rows = useMemo(() => [...(trades.data?.items ?? []), ...extraRows], [trades.data, extraRows]);

  const apply = useCallback(() => {
    setApplied(draft);
    setExtraRows([]);
    setCursor(null);
  }, [draft]);

  const loadMore = useCallback(async () => {
    const next = trades.data?.next_cursor ?? null;
    if (next === null) return;
    setLoadingMore(true);
    try {
      const result = await api.getClosedTrades(filters, next);
      setExtraRows((prev) => [...prev, ...result.data.items]);
      setCursor(result.data.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }, [api, filters, trades.data]);

  const columns: readonly TableColumn<ClosedTradeRow, 'id' | 'symbol' | 'strategy' | 'regime' | 'times' | 'net' | 'outcome' | 'exit'>[] = [
    { key: 'id', header: 'Completed trade', render: (row) => <span className="text-mono">{row.completed_trade_id}</span> },
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
    { key: 'strategy', header: 'Strategy', sortValue: (row) => row.strategy_version, render: (row) => <span className="text-mono">{row.strategy_version}</span> },
    {
      key: 'regime',
      header: 'Regime',
      sortValue: (row) => row.regime?.trend_strength ?? null,
      render: (row) => (row.regime === null ? <Unavailable /> : `${row.regime.trend_strength} / ${row.regime.volatility_bucket} / ${row.regime.session_phase}`),
    },
    {
      key: 'times',
      header: 'Entry → exit (holding)',
      sortValue: (row) => row.exit_timestamp,
      render: (row) => (
        <span className="text-mono" style={{ fontSize: 11.5 }}>
          {formatDateTimeWithZone(row.entry_timestamp)} → {formatDateTimeWithZone(row.exit_timestamp)}
          <div className="text-faint">{formatDuration(row.holding_time_ms)}</div>
        </span>
      ),
    },
    {
      key: 'net',
      header: 'Gross → net',
      align: 'right',
      sortValue: (row) => row.net_realized_pnl,
      render: (row) => (
        <span className="text-mono">
          {formatSigned(row.gross_realized_pnl)} → <span className={row.net_realized_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatSigned(row.net_realized_pnl)}</span>
          <div className="text-faint">
            comm {formatNumber(row.commission, 2)} · swap {formatNumber(row.swap, 2)} · fees {formatNumber(row.fees, 2)}
          </div>
        </span>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome / R',
      sortValue: (row) => row.outcome,
      render: (row) => (
        <>
          <Badge tone={toneForOutcome(row.outcome)}>{OUTCOME_LABELS[row.outcome]}</Badge>{' '}
          {row.r_multiple === null ? <Unavailable /> : <span className="text-mono">{formatNumber(row.r_multiple, 2)}R</span>}
        </>
      ),
    },
    {
      key: 'exit',
      header: 'Exit reason / tags',
      sortValue: (row) => row.exit_reason,
      render: (row) => (
        <>
          <div>{EXIT_REASON_LABELS[row.exit_reason]}</div>
          <div>
            {row.tags.map((tag) => (
              <span className="tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Trades &amp; analytics</h1>
          <div className="page-header__spec">§3.2 B · filters, CSV export, required charts and the per-trade decision trail</div>
        </div>
        <CsvExportButton
          rows={rows}
          filename="poseidon-closed-trades.csv"
          columns={[
            { header: 'completed_trade_id', value: (row) => row.completed_trade_id },
            { header: 'decision_id', value: (row) => row.decision_id },
            { header: 'strategy_version', value: (row) => row.strategy_version },
            { header: 'symbol', value: (row) => row.symbol },
            { header: 'direction', value: (row) => row.direction },
            { header: 'entry_timestamp', value: (row) => row.entry_timestamp },
            { header: 'average_entry_price', value: (row) => row.average_entry_price },
            { header: 'entered_volume', value: (row) => row.entered_volume },
            { header: 'exit_timestamp', value: (row) => row.exit_timestamp },
            { header: 'average_exit_price', value: (row) => row.average_exit_price },
            { header: 'exited_volume', value: (row) => row.exited_volume },
            { header: 'gross_realized_pnl', value: (row) => row.gross_realized_pnl },
            { header: 'commission', value: (row) => row.commission },
            { header: 'swap', value: (row) => row.swap },
            { header: 'fees', value: (row) => row.fees },
            { header: 'net_realized_pnl', value: (row) => row.net_realized_pnl },
            { header: 'initial_risk_cash', value: (row) => row.initial_risk_cash },
            { header: 'r_multiple', value: (row) => row.r_multiple },
            { header: 'exit_reason', value: (row) => row.exit_reason },
            { header: 'outcome', value: (row) => row.outcome },
            { header: 'holding_time_ms', value: (row) => row.holding_time_ms },
            { header: 'order_ticket', value: (row) => row.order_ticket },
            { header: 'position_ticket', value: (row) => row.position_ticket },
            { header: 'deal_tickets', value: (row) => row.deal_tickets.join('|') },
            { header: 'tags', value: (row) => row.tags.join('|') },
          ]}
        />
      </header>

      <Panel
        title="Filters"
        subtitle="applied to the trade list and to every chart below"
        mock={isMock}
        footnote="The daemon applies these server-side. Client-side filtering is deliberately not used, so a chart and the table can never disagree about the window."
      >
        <div className="field-row">
          <label>
            From
            <input type="date" value={draft.from} onChange={(event) => setDraft({ ...draft, from: event.target.value })} />
          </label>
          <label>
            To
            <input type="date" value={draft.to} onChange={(event) => setDraft({ ...draft, to: event.target.value })} />
          </label>
          <MultiSelect label="Session" options={[...ALL_SESSIONS]} selected={draft.sessions} onChange={(next) => setDraft({ ...draft, sessions: next })} />
          <MultiSelect label="Strategy" options={[...ALL_STRATEGIES]} selected={draft.strategies} onChange={(next) => setDraft({ ...draft, strategies: next })} />
          <MultiSelect label="Symbol" options={ALL_SYMBOLS} selected={draft.symbols} onChange={(next) => setDraft({ ...draft, symbols: next })} />
          <MultiSelect label="Outcome" options={[...ALL_OUTCOMES]} selected={draft.outcomes} onChange={(next) => setDraft({ ...draft, outcomes: next })} />
          <MultiSelect label="Exit reason" options={[...ALL_EXIT_REASONS]} selected={draft.exit_reasons} onChange={(next) => setDraft({ ...draft, exit_reasons: next })} />
          <button type="button" className="btn btn--primary" onClick={apply}>
            Apply filters
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDraft(EMPTY_DRAFT);
              setApplied(EMPTY_DRAFT);
              setExtraRows([]);
              setCursor(null);
            }}
          >
            Clear
          </button>
        </div>
        <p className="text-faint" style={{ marginTop: 8, fontSize: 11.5 }}>
          Session values are sent as the daemon's identifiers (<span className="text-mono">opening</span>,{' '}
          <span className="text-mono">mid_session</span>, <span className="text-mono">pre_close</span>), matching the §18.2 regime classifier's{' '}
          <span className="text-mono">session_phase</span>.
        </p>
      </Panel>

      <Panel
        title="Completed trades"
        subtitle={trades.data === null ? 'loading' : `${rows.length} of ${trades.data.total} matching trades loaded`}
        mock={isMock}
        flush
        actions={
          trades.data?.next_cursor !== null && trades.data?.next_cursor !== undefined ? (
            <button type="button" className="btn btn--sm" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          ) : (
            <span className="text-faint" style={{ fontSize: 11.5 }}>
              all matching trades loaded{cursor === null ? '' : ' (cursor exhausted)'}
            </span>
          )
        }
        footnote="Click any row to open the full decision trail (§3.2 B)."
      >
        {trades.error !== null && trades.data === null ? (
          <ErrorState error={trades.error} context="Closed trades could not be loaded." onRetry={trades.refresh} />
        ) : trades.data === null ? (
          <LoadingState label="Loading closed trades…" />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.completed_trade_id}
            initialSort={{ key: 'times', direction: 'desc' }}
            onRowClick={(row) => navigate(`/trades/${row.completed_trade_id}`)}
            emptyTitle="No trades match these filters"
            emptyMessage="Widen the date range or clear a filter."
          />
        )}
      </Panel>

      <div className="grid grid--2">
        {CHART_IDS.map((chartId) => (
          <ChartSlot key={chartId} chartId={chartId} filters={filters} mock={isMock} />
        ))}
      </div>

      <Panel title="Chart coverage" subtitle="what each required chart needs from the daemon">
        <ul className="consequences" style={{ paddingLeft: 18 }}>
          <li>Intraday cumulative realized P&L — running sum of net realized P&L, ordered by exit time.</li>
          <li>Equity curve — realized and marked equity from account snapshots, not reconstructed in the browser.</li>
          <li>Win/loss distribution — the §3.3 outcome buckets after costs, with breakeven shown separately.</li>
          <li>P&amp;L by hour and weekday — broker-local session hours, stated explicitly on the chart.</li>
          <li>P&amp;L by instrument, strategy version and market regime — regime taken from the frozen snapshot, not recomputed.</li>
          <li>Actual slippage distribution — points against the decision quote, from broker deals.</li>
          <li>Holding-time distribution — first entry fill to final exit fill.</li>
          <li>Consecutive wins/losses — signed streak series plus the longest streak in each direction.</li>
          <li>Trade count and win rate by session — using the same win definition as the KPI cards.</li>
        </ul>
      </Panel>
    </>
  );
}
